import React, { useEffect, useState, useCallback } from 'react';
import { useWatch } from 'react-hook-form';
import { useResourceContext, useDataProvider, useNotify, useInput } from 'react-admin';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, IconButton, Checkbox, Button, Paper, Typography, Box, TablePagination,
  Select, MenuItem, FormControl
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

const OperationPlanMultiInput = ({ source = 'selected_plans', field_meta }) => {
  const resource = useResourceContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  // Watch form fields for filtering
  const education_area_id = useWatch({ name: 'education_area_id' });
  const education_zone_id = useWatch({ name: 'education_zone_id' });
  const vendor_id = useWatch({ name: 'vendor_id' });
  const workflow_subcategory = useWatch({ name: 'workflow_subcategory' });

  const [plans, setPlans] = useState([]);
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState('');

  // Column configuration for the first grid (read-only selection)
  const selectionColumns = [
    { field: 'id', label: 'رقم السجل ODS' },
    { field: 'operationnumber', label: 'الرقم التشغيلي' },
    { field: 'plateletters', label: 'حروف اللوحة' },
    { field: 'platenumbers', label: 'أرقام اللوحة' },
    { field: 'scname', label: 'أسم المدرسة' },
    { field: 'name', label: 'أسم السائق' },
    { field: 'nid_number', label: 'رقم الهوية' },
  ];

  // Column configuration for editable fields in the second grid
  // Can be overridden from field_meta JSON config
  const defaultEditableFields = [
    { 
      field: 'exterior_structure_receive', 
      label: 'الهيكل الخارجي (استلام)', 
      type: 'select',
      options: ['سليم', 'خدوش طفيفة', 'تلف', 'صدأ', 'ثقوب', 'بحاجة إلى تصليح'],
      category: 'receive'
    },
    { 
      field: 'exterior_structure_deliver', 
      label: 'الهيكل الخارجي (تسليم)', 
      type: 'select',
      options: ['سليم', 'خدوش طفيفة', 'تلف', 'صدأ', 'ثقوب', 'بحاجة إلى تصليح'],
      category: 'deliver'
    },
    { 
      field: 'tires_receive', 
      label: 'الإطارات (استلام)', 
      type: 'select',
      options: ['سليمة', 'غير سليمة'],
      category: 'receive'
    },
    { 
      field: 'tires_deliver', 
      label: 'الإطارات (تسليم)', 
      type: 'select',
      options: ['سليمة', 'غير سليمة'],
      category: 'deliver'
    },
    { 
      field: 'lights_receive', 
      label: 'الأنوار الأمامية / الخلفية (استلام)', 
      type: 'select',
      options: ['سليمة', 'تعطل', 'مصابيح توجيه لا تعمل', 'فلاش لا يعمل', 'مصابيح مكسورة'],
      category: 'receive'
    },
    { 
      field: 'lights_deliver', 
      label: 'الأنوار الأمامية / الخلفية (تسليم)', 
      type: 'select',
      options: ['سليمة', 'تعطل', 'مصابيح توجيه لا تعمل', 'فلاش لا يعمل', 'مصابيح مكسورة'],
      category: 'deliver'
    },
    { 
      field: 'fire_extinguisher_receive', 
      label: 'طفاية الحريق (استلام)', 
      type: 'select',
      options: ['موجودة وصالحة', 'موجودة وتحتاج صيانة', 'غير موجودة', 'منتهية الصلاحية'],
      category: 'receive'
    },
    { 
      field: 'fire_extinguisher_deliver', 
      label: 'طفاية الحريق (تسليم)', 
      type: 'select',
      options: ['موجودة وصالحة', 'موجودة وتحتاج صيانة', 'غير موجودة', 'منتهية الصلاحية'],
      category: 'deliver'
    },
    { 
      field: 'first_aid_receive', 
      label: 'أدوات الإسعافات الأولية (استلام)', 
      type: 'select',
      options: ['موجودة', 'ناقصة', 'منتهية الصلاحية', 'غير موجودة'],
      category: 'receive'
    },
    { 
      field: 'first_aid_deliver', 
      label: 'أدوات الإسعافات الأولية (تسليم)', 
      type: 'select',
      options: ['موجودة', 'ناقصة', 'منتهية الصلاحية', 'غير موجودة'],
      category: 'deliver'
    },
    { 
      field: 'fuel_receive', 
      label: 'الوقود الكمية التقريبية (استلام)', 
      type: 'select',
      options: ['من 75% إلى 100%', 'من 40% إلى 75%', 'أقل من 40%', 'في طور التعبئة'],
      category: 'receive'
    },
    { 
      field: 'fuel_deliver', 
      label: 'الوقود الكمية التقريبية (تسليم)', 
      type: 'select',
      options: ['من 75% إلى 100%', 'من 40% إلى 75%', 'أقل من 40%', 'في طور التعبئة'],
      category: 'deliver'
    },
    { 
      field: 'cleanliness_receive', 
      label: 'نظافة الحافلة (استلام)', 
      type: 'select',
      options: ['نظيفة جدًا', 'نظيفة ومقبولة', 'تحتاج تنظيف داخلي', 'تحتاج غسل خارجي', 'غير مقبولة (نفايات / روائح)'],
      category: 'receive'
    },
    { 
      field: 'cleanliness_deliver', 
      label: 'نظافة الحافلة (تسليم)', 
      type: 'select',
      options: ['نظيفة جدًا', 'نظيفة ومقبولة', 'تحتاج تنظيف داخلي', 'تحتاج غسل خارجي', 'غير مقبولة (نفايات / روائح)'],
      category: 'deliver'
    },
    { 
      field: 'ac_receive', 
      label: 'أجهزة التكييف (استلام)', 
      type: 'select',
      options: ['تعمل جيدًا', 'ضعف التبريد', 'لا تعمل', 'بحاجة إلى صيانة'],
      category: 'receive'
    },
    { 
      field: 'ac_deliver', 
      label: 'أجهزة التكييف (تسليم)', 
      type: 'select',
      options: ['تعمل جيدًا', 'ضعف التبريد', 'لا تعمل', 'بحاجة إلى صيانة'],
      category: 'deliver'
    },
    { 
      field: 'notes_receive', 
      label: 'الملاحظات (استلام)', 
      type: 'text',
      category: 'receive'
    },
    { 
      field: 'notes_deliver', 
      label: 'الملاحظات (تسليم)', 
      type: 'text',
      category: 'deliver'
    },
  ];

  // Parse editable fields from field_meta or use default
  const editableFields = React.useMemo(() => {
    let fields = defaultEditableFields;
    
    if (field_meta && field_meta.editable_fields) {
      try {
        fields = typeof field_meta.editable_fields === 'string' 
          ? JSON.parse(field_meta.editable_fields) 
          : field_meta.editable_fields;
      } catch (e) {
        console.error('Error parsing editable_fields from field_meta:', e);
      }
    }
    
    // Return all fields (both receive and deliver)
    return fields;
  }, [field_meta]);

  // Group fields by base field name (exterior_structure, tires, etc.)
  const groupedFields = React.useMemo(() => {
    const groups = [];
    const processed = new Set();
    
    editableFields.forEach(field => {
      // Extract base field name (remove _receive or _deliver)
      const baseName = field.field.replace(/_receive$|_deliver$/, '');
      
      if (!processed.has(baseName)) {
        processed.add(baseName);
        
        // Find both receive and deliver fields for this base name
        const receiveField = editableFields.find(f => f.field === `${baseName}_receive`);
        const deliverField = editableFields.find(f => f.field === `${baseName}_deliver`);
        
        if (receiveField && deliverField) {
          groups.push({
            baseName,
            label: receiveField.label.replace(' (استلام)', '').replace(' (تسليم)', ''),
            receiveField,
            deliverField
          });
        }
      }
    });
    
    return groups;
  }, [editableFields]);

  // Validation function - returns error message or undefined
  const validatePlans = useCallback((value) => {
    const plans = value || [];
    const isReceiveMode = workflow_subcategory === 4 || workflow_subcategory === '4';
    const numSubcategory = parseInt(workflow_subcategory);
    const isDeliverMode = !isNaN(numSubcategory) && numSubcategory === 9;
    
    if (plans.length === 0) {
      return 'الرجاء اختيار خطة تشغيلية واحدة على الأقل';
    }

    for (const plan of plans) {
      for (const group of groupedFields) {
        // Check receive fields in receive mode
        if (isReceiveMode) {
          const fieldValue = plan[group.receiveField.field];
          if (!fieldValue || fieldValue.trim() === '') {
            return `الرجاء تعبئة حقل "${group.label}" (استلام) للرقم التشغيلي ${plan.operationnumber || plan.id}`;
          }
        }
        
        // Check deliver fields in deliver mode
        if (isDeliverMode) {
          const fieldValue = plan[group.deliverField.field];
          if (!fieldValue || fieldValue.trim() === '') {
            return `الرجاء تعبئة حقل "${group.label}" (تسليم) للرقم التشغيلي ${plan.operationnumber || plan.id}`;
          }
        }
      }
    }
    
    return undefined; // No error
  }, [workflow_subcategory, groupedFields]);

  // Use react-admin's useInput hook for proper form integration
  const {
    field,
    fieldState: { error, invalid },
    isRequired,
  } = useInput({
    source,
    validate: validatePlans,
  });

  const selectedPlansData = field.value || [];

  // Calculate yesterday's date
  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  };

  // Fetch operation plans based on filters
  const fetchOperationPlans = async () => {
    if (!education_area_id || !education_zone_id || !vendor_id) {
      return; // Don't fetch if required fields are missing
    }

    setLoading(true);
    try {
      const yesterdayDate = getYesterdayDate();
      
      const response = await dataProvider.getList('ods_plan_history', {
        filter: {
          'sector_id@_eq': education_area_id,
          'administration_id@_eq': education_zone_id,
          'operator_id@_eq': vendor_id,
          'sub_day@_eq': yesterdayDate,
        },
        pagination: { page: page + 1, perPage: rowsPerPage },
        sort: { field: 'id', order: 'DESC' },
      });

      if (response.data && response.data.length > 0) {
        setPlans(response.data);
        setTotal(response.total || response.data.length);
      } else {
        setPlans([]);
        setTotal(0);
        if (page === 0) {
          notify('لا توجد خطط تشغيلية لليوم السابق مع الفلاتر المحددة', { type: 'info' });
        }
      }
    } catch (error) {
      notify('خطأ في جلب الخطط التشغيلية', { type: 'error' });
      setPlans([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch operation plans when filter values change
  useEffect(() => {
    if (education_area_id && education_zone_id && vendor_id) {
      fetchOperationPlans();
    }
  }, [education_area_id, education_zone_id, vendor_id, page, rowsPerPage]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [education_area_id, education_zone_id, vendor_id]);

  useEffect(() => {
    const plans = Array.isArray(selectedPlansData) ? selectedPlansData : [];
    setSelectedPlans(plans);
  }, [selectedPlansData]);

  // Check if plan is already selected
  const isPlanSelected = (planId) => {
    return Array.isArray(selectedPlans) && selectedPlans.some(sp => sp.id === planId);
  };

  // Handle checkbox toggle
  const handleToggleSelect = (plan) => {
    // Disable selection/deselection during delivery (workflow_subcategory >= 9)
    const numSubcategory = parseInt(workflow_subcategory);
    if (!isNaN(numSubcategory) && numSubcategory >= 9) {
      notify('لا يمكن الاختيار أو الحذف في وضع التسليم أو بعده', { type: 'warning' });
      return;
    }
    
    let updatedSelected;
    
    if (isPlanSelected(plan.id)) {
      // Remove from selected
      updatedSelected = (selectedPlans || []).filter(sp => sp.id !== plan.id);
    } else {
      // Add to selected with initial editable field values
      // Double-check it's not already in the array to prevent duplicates
      const alreadyExists = (selectedPlans || []).some(sp => sp.id === plan.id);
      if (alreadyExists) {
        return; // Don't add duplicate
      }
      
      // Create new plan with only essential fields
      const newPlan = {
        id: plan.id,
        operationnumber: plan.operationnumber,
        plateletters: plan.plateletters,
        platenumbers: plan.platenumbers,
        scname: plan.scname,
        name: plan.name,
        nid_number: plan.nid_number,
      };
      
      // Initialize all editable fields
      editableFields.forEach(field => {
        newPlan[field.field] = '';
      });
      
      updatedSelected = [...(selectedPlans || []), newPlan];
    }

    setSelectedPlans(updatedSelected);
    field.onChange(updatedSelected);
  };

  // Handle text field change in the second grid
  const handleFieldChange = (planId, fieldName, value) => {
    const updatedSelected = (selectedPlans || []).map(plan => {
      if (plan.id === planId) {
        return { ...plan, [fieldName]: value };
      }
      return plan;
    });
    
    setSelectedPlans(updatedSelected);
    field.onChange(updatedSelected);
  };

  // Handle delete from selected grid
  const handleDeleteSelected = (planId) => {
    // Disable deletion during delivery (workflow_subcategory >= 9)
    const numSubcategory = parseInt(workflow_subcategory);
    if (!isNaN(numSubcategory) && numSubcategory >= 9) {
      notify('لا يمكن الحذف في وضع التسليم أو بعده', { type: 'warning' });
      return;
    }
    
    const updatedSelected = (selectedPlans || []).filter(sp => sp.id !== planId);
    setSelectedPlans(updatedSelected);
    field.onChange(updatedSelected);
  };

  // Filter plans based on search text
  const filteredPlans = React.useMemo(() => {
    if (!searchText.trim()) return plans;
    
    const searchLower = searchText.toLowerCase();
    return plans.filter(plan => {
      return (
        (plan.id && plan.id.toString().includes(searchLower)) ||
        (plan.operationnumber && plan.operationnumber.toLowerCase().includes(searchLower)) ||
        (plan.plateletters && plan.plateletters.toLowerCase().includes(searchLower)) ||
        (plan.platenumbers && plan.platenumbers.toLowerCase().includes(searchLower)) ||
        (plan.scname && plan.scname.toLowerCase().includes(searchLower)) ||
        (plan.name && plan.name.toLowerCase().includes(searchLower)) ||
        (plan.nid_number && plan.nid_number.toString().includes(searchLower))
      );
    });
  }, [plans, searchText]);

  return (
    <>
      {/* Validation Error Display */}
 
      
      {/* First Grid - Selection - Only show in receive mode */}
      {(workflow_subcategory === 4 || workflow_subcategory === '4') && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            اختيار  الحافلات {loading && '(جاري التحميل...)'}
          </Typography>
        
        {/* Search Input */}
        {plans && plans.length > 0 && (
          <TextField
            fullWidth
            variant="outlined"
            placeholder="البحث (رقم السجل، الرقم التشغيلي، اللوحة، المدرسة، السائق، رقم الهوية)"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ mb: 2 }}
            size="small"
          />
        )}
        
        {plans && plans.length > 0 ? (
          <Box sx={{ width: '95vw', maxWidth: '95vw' }}>
            <TableContainer component={Paper} sx={{ maxHeight: '400px', overflow: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>اختيار</TableCell>
                    {selectionColumns.map((col) => (
                      <TableCell
                        key={col.field}
                        sx={{
                          textAlign: 'center',
                          whiteSpace: 'nowrap',
                          minWidth: '100px',
                          fontWeight: 'bold',
                        }}
                      >
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPlans.map((plan) => (
                    <TableRow key={plan.id}>
                      <TableCell>
                        <Checkbox
                          checked={isPlanSelected(plan.id)}
                          onChange={() => handleToggleSelect(plan)}
                          disabled={!isNaN(parseInt(workflow_subcategory)) && parseInt(workflow_subcategory) >= 9}
                        />
                      </TableCell>
                      {selectionColumns.map((col) => (
                        <TableCell
                          key={col.field}
                          sx={{
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                            minWidth: '100px',
                          }}
                        >
                          <span>{plan[col.field] || '--'}</span>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="عدد الصفوف في الصفحة:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
            />
          </Box>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', fontSize: '16px', color: 'gray' }}>
            {loading 
              ? 'جاري تحميل الخطط التشغيلية...' 
              : !education_area_id || !education_zone_id || !vendor_id
                ? 'الرجاء تحديد منطقة التعليم، المنطقة التعليمية، والمقاول لعرض الخطط التشغيلية'
                : 'لا يوجد خطة تشغيلية لليوم السابق مع الفلاتر المحددة'
            }
          </div>
        )}
      </Box>
      )}

      {/* Second Grid - Selected Plans with Editable Fields */}
      {selectedPlans && selectedPlans.length > 0 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            الحافلات المحددة ({selectedPlans.length})
          </Typography>
          
          {/* Status Message - Only show in receive or deliver mode */}
          {(workflow_subcategory === 4 || workflow_subcategory === '4' || (!isNaN(parseInt(workflow_subcategory)) && parseInt(workflow_subcategory) >= 9)) && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                {workflow_subcategory === 4 || workflow_subcategory === '4' 
                  ? '📋 وضع الاستلام: قم بتعبئة بيانات استلام الحافلات'
                  : parseInt(workflow_subcategory) === 9
                    ? '📋 وضع التسليم: قم بتعبئة بيانات تسليم الحافلات '
                    : '📋 وضع القراءة فقط: عرض بيانات الاستلام والتسليم '}
              </Typography>
            </Box>
          )}
          
          <Box sx={{ width: '95vw', maxWidth: '95vw' }}>
            <TableContainer component={Paper} sx={{ maxHeight: '400px', overflow: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap',  fontWeight: 'bold' }}>الرقم التشغيلي</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap',  fontWeight: 'bold' }}>اللوحة</TableCell>
                    {groupedFields.map((group) => (
                      <TableCell
                        key={group.baseName}
                        sx={{
                          textAlign: 'center',
                          whiteSpace: 'nowrap',
                          minWidth: '200px',
                          fontWeight: 'bold',
                        }}
                      >
                        {group.label}
                      </TableCell>
                    ))}
                    <TableCell sx={{ whiteSpace: 'nowrap',  fontWeight: 'bold' }}>حذف</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedPlans.map((plan) => (
                    <TableRow key={plan.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {plan.operationnumber || '--'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {`${plan.platenumbers || ''} ${plan.plateletters || ''}`}
                      </TableCell>
                      {groupedFields.map((group) => {
                        const isReceiveMode = workflow_subcategory === 4 || workflow_subcategory === '4';
                        const numSubcategory = parseInt(workflow_subcategory);
                        const isDeliverMode = !isNaN(numSubcategory) && numSubcategory === 9;
                        const isDeliverReadOnly = !isNaN(numSubcategory) && numSubcategory > 9;
                        
                        return (
                          <TableCell key={group.baseName}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {/* Receive Field - Editable in receive mode, read-only in deliver mode */}
                              {isReceiveMode && (
                                <Box>
                                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold', color: '#1976d2' }}>
                                    استلام
                                  </Typography>
                                  {group.receiveField.type === 'select' && group.receiveField.options ? (
                                    <FormControl 
                                      fullWidth 
                                      size="small" 
                                      required
                                      error={!plan[group.receiveField.field] || plan[group.receiveField.field] === ''}
                                    >
                                      <Select
                                        value={plan[group.receiveField.field] || ''}
                                        onChange={(e) => handleFieldChange(plan.id, group.receiveField.field, e.target.value)}
                                        variant="outlined"
                                        displayEmpty
                                        required
                                        error={!plan[group.receiveField.field] || plan[group.receiveField.field] === ''}
                                      >
                                        <MenuItem value="">
                                          <em>-- اختر --</em>
                                        </MenuItem>
                                        {group.receiveField.options.map((option) => (
                                          <MenuItem key={option} value={option}>
                                            {option}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  ) : (
                                    <TextField
                                      value={plan[group.receiveField.field] || ''}
                                      onChange={(e) => handleFieldChange(plan.id, group.receiveField.field, e.target.value)}
                                      size="small"
                                      fullWidth
                                      variant="outlined"
                                      required
                                      error={!plan[group.receiveField.field] || plan[group.receiveField.field].trim() === ''}
                                      helperText={group.receiveField.field !== 'notes_receive' && (!plan[group.receiveField.field] || plan[group.receiveField.field].trim() === '') ? 'إجباري' : ''}
                                    />
                                  )}
                                </Box>
                              )}
                              
                              {/* Show receive data as read-only text in deliver mode */}
                              {!isReceiveMode && (
                                <Box>
                                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold', color: '#757575' }}>
                                    استلام
                                  </Typography>
                                  <Typography variant="body2" sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1, minHeight: '40px' }}>
                                    {plan[group.receiveField.field] || '--'}
                                  </Typography>
                                </Box>
                              )}
                              
                              {/* Deliver Field - Editable when workflow_subcategory === 9 */}
                              {isDeliverMode && (
                                <Box>
                                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold', color: '#d32f2f' }}>
                                    تسليم
                                  </Typography>
                                  {group.deliverField.type === 'select' && group.deliverField.options ? (
                                    <FormControl 
                                      fullWidth 
                                      size="small" 
                                      required
                                      error={!plan[group.deliverField.field] || plan[group.deliverField.field] === ''}
                                    >
                                      <Select
                                        value={plan[group.deliverField.field] || ''}
                                        onChange={(e) => handleFieldChange(plan.id, group.deliverField.field, e.target.value)}
                                        variant="outlined"
                                        displayEmpty
                                        required
                                        error={!plan[group.deliverField.field] || plan[group.deliverField.field] === ''}
                                      >
                                        <MenuItem value="">
                                          <em>-- اختر --</em>
                                        </MenuItem>
                                        {group.deliverField.options.map((option) => (
                                          <MenuItem key={option} value={option}>
                                            {option}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  ) : (
                                    <TextField
                                      value={plan[group.deliverField.field] || ''}
                                      onChange={(e) => handleFieldChange(plan.id, group.deliverField.field, e.target.value)}
                                      size="small"
                                      fullWidth
                                      variant="outlined"
                                      required
                                      error={!plan[group.deliverField.field] || plan[group.deliverField.field].trim() === ''}
                                      helperText={group.deliverField.field !== 'notes_deliver' && (!plan[group.deliverField.field] || plan[group.deliverField.field].trim() === '') ? 'إجباري' : ''}
                                    />
                                  )}
                                </Box>
                              )}
                              
                              {/* Show deliver data as read-only text when workflow_subcategory > 9 */}
                              {isDeliverReadOnly && (
                                <Box>
                                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold', color: '#757575' }}>
                                    تسليم
                                  </Typography>
                                  <Typography variant="body2" sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1, minHeight: '40px' }}>
                                    {plan[group.deliverField.field] || '--'}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </TableCell>
                        );
                      })}
                      <TableCell>
                        <IconButton 
                          onClick={() => handleDeleteSelected(plan.id)}
                          color="error"
                          size="small"
                          disabled={!isNaN(parseInt(workflow_subcategory)) && parseInt(workflow_subcategory) >= 9}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      )}
    </>
  );
};

export default OperationPlanMultiInput;
